import { z } from 'zod'

/**
 * บริการเสริม: what a bill or a job adds to its freight — carrying the goods upstairs — and how each is priced, set
 * here rather than in the app (`services`). A bill or job copies one into a line (`ServiceLine`) whose price stays the
 * line's, and the line's amount follows what the line is for (`serviceAmount`): per bill, per piece or per `step` kg
 * begun of the pieces or kg typed on the line, never the bill's goods, as what goes upstairs may be part of them. The
 * goods are not insured: what the company pays for loss or damage is the shipping terms' (terms.ts), not a service.
 *
 * Freight within Thailand is exempt from VAT; a service is not (`taxable`). Prices include VAT, so what a customer pays
 * stays the same the day the company registers for VAT: the VAT is then drawn out of the taxable lines.
 */
export const serviceBases = ['bill', 'piece', 'weight'] as const
export type ServiceBasis = (typeof serviceBases)[number]

/** How a service is priced: what a line copies from it */
const pricing = {
  basis: z.enum(serviceBases).default('bill'),
  /** Baht per bill, per piece or per `step` kg */
  rate: z.number().min(0),
  /** `weight`: kg per `rate`, begun steps counted whole (1 baht per 5 kg: 12 kg is 3 baht) */
  step: z.number().positive().nullish(),
  /** The least it costs */
  minimum: z.number().min(0).nullish(),
  taxable: z.boolean().default(true),
}

/** Whether a line is priced on pieces or kg it says (`quantity`): every basis but per bill */
export const servicePerQuantity = (line: Pick<ServiceLine, 'basis'>) => line.basis !== 'bill'

/** A service on a bill or job, its price as copied; `serviceId` empty once the service is gone */
export const serviceLineSchema = z
  .object({
    serviceId: z.string().nullish(),
    name: z.string().trim().min(1).max(200),
    ...pricing,
    /** `piece`: how many pieces, `weight`: how many kg the service is for; typed on the line */
    quantity: z.number().positive().nullish(),
  })
  .superRefine((line, ctx) => {
    if (servicePerQuantity(line) && line.quantity == null)
      ctx.addIssue({ code: 'custom', path: ['quantity'], message: line.basis === 'weight' ? 'ใส่น้ำหนักที่ต้องขน' : 'ใส่จำนวนชิ้น' })
  })
export type ServiceLine = z.infer<typeof serviceLineSchema>

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/** A line's amount: its rate by the basis on the pieces or kg it is for, never below its minimum */
export function serviceAmount(line: Pick<ServiceLine, 'basis' | 'rate' | 'step' | 'minimum' | 'quantity'>): number {
  const rate = Number(line.rate) || 0
  const quantity = Number(line.quantity) || 0
  const steps = (kg: number, step: number) => Math.ceil(Math.round((kg / step) * 1e6) / 1e6)
  const priced = line.basis === 'piece' ? rate * quantity : line.basis === 'weight' ? rate * steps(quantity, Number(line.step) || 1) : rate
  return round2(Math.max(priced, Number(line.minimum) || 0))
}

/** What the services of a bill add to it */
export function servicesFeeOf(lines: readonly ServiceLine[] | null | undefined) {
  return round2((lines ?? []).reduce((sum, line) => sum + serviceAmount(line), 0))
}

/** A service the company offers, priced as a line copies it */
export interface Service extends Omit<ServiceLine, 'serviceId' | 'quantity'> {
  /** What a line keeps (`serviceId`): never changed once bills have it */
  id: string
}

/**
 * The add-on services offered, oldest first: set in code, not in the app (owner, 1 Nov 2569). A new price is a change
 * here; bills already made keep the price their lines copied
 */
export const services: readonly Service[] = [
  { id: 'upstairs', name: 'ขนสินค้าขึ้นชั้น 2-3', basis: 'weight', rate: 0.5, step: 1, minimum: 5, taxable: true },
]

/** A line for a picked service: its name and price as they are now, how many pieces or kg still to type */
export const serviceLineOf = ({ id, ...price }: Service): ServiceLine => ({ serviceId: id, ...price, quantity: null })
